import { useCallback, useEffect, useRef, useState } from 'react';
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from 'react-places-autocomplete';
import { useGoogleMaps } from './GoogleMapsContext';

const NPPlacesInput = ({
  value,
  onResultSelected,
  onInputChange,
  placeholder,
}) => {
  const { isLoaded, loadError } = useGoogleMaps();
  const [address, setAddress] = useState(value);
  const geocodeRequestIdRef = useRef(0);
  const shouldDebounceGeocodeRef = useRef(false);
  const onResultSelectedRef = useRef(onResultSelected);

  useEffect(() => {
    onResultSelectedRef.current = onResultSelected;
  }, [onResultSelected]);

  const resolveAddress = useCallback((selectedAddress, requestId) => {
    geocodeByAddress(selectedAddress)
      .then((results) => {
        if (requestId !== geocodeRequestIdRef.current || !results[0]) {
          return null;
        }

        return getLatLng(results[0]);
      })
      .then((latlng) => {
        if (!latlng || requestId !== geocodeRequestIdRef.current) return;

        onResultSelectedRef.current(selectedAddress, latlng);
      })
      .catch((error) => {
        if (requestId === geocodeRequestIdRef.current) {
          console.error('Error getting latLng', error);
        }
      });
  }, []);

  useEffect(() => {
    if (!shouldDebounceGeocodeRef.current || !address.trim()) return;

    const timeoutId = setTimeout(() => {
      const requestId = geocodeRequestIdRef.current + 1;
      geocodeRequestIdRef.current = requestId;
      resolveAddress(address, requestId);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [address, resolveAddress]);

  useEffect(
    () => () => {
      geocodeRequestIdRef.current += 1;
    },
    []
  );

  const handleChange = (nextAddress) => {
    geocodeRequestIdRef.current += 1;
    shouldDebounceGeocodeRef.current = true;
    setAddress(nextAddress);
    onInputChange?.(nextAddress);
  };

  const handleSelect = (selectedAddress) => {
    const requestId = geocodeRequestIdRef.current + 1;
    geocodeRequestIdRef.current = requestId;
    shouldDebounceGeocodeRef.current = false;
    setAddress(selectedAddress);
    onInputChange?.(selectedAddress);
    resolveAddress(selectedAddress, requestId);
  };

  if (loadError) {
    return <div>Error loading maps</div>;
  }

  if (!isLoaded) {
    return <div>Loading...</div>;
  }

  return (
    <PlacesAutocomplete
      value={address}
      onChange={handleChange}
      onSelect={handleSelect}
      searchOptions={{ componentRestrictions: { country: 'CA' } }}
    >
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
        <div className="autocomplete" style={{ position: 'relative' }}>
          <input
            required
            {...getInputProps({
              placeholder,
              className: 'form-control',
            })}
          />
          <div
            className="autocomplete-items"
            style={{
              position: 'absolute',
              zIndex: 999,
              width: '100%',
            }}
          >
            {loading && <div>Loading...</div>}
            {suggestions.map((suggestion, index) => {
              const style = suggestion.active
                ? {
                    backgroundColor: 'rgb(214, 228, 249)',
                    cursor: 'pointer',
                    padding: '7px',
                  }
                : {
                    backgroundColor: '#ffffff',
                    cursor: 'pointer',
                    padding: '7px',
                  };
              return (
                <div
                  {...getSuggestionItemProps(suggestion, { style })}
                  key={index}
                >
                  {suggestion.description}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </PlacesAutocomplete>
  );
};

export default NPPlacesInput;
